import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useTimeoutSettings from '../../hooks/useTimeoutSettings';
import { FaClock, FaInfoCircle, FaUserClock, FaSignOutAlt } from 'react-icons/fa';

const Settings = () => {
  const history = useHistory();
  const sessionUser = useSelector(state => state.session.user);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Use our custom hook for timeout settings
  const {
    timeoutHours,
    isLoading,
    error,
    updateTimeoutSetting
  } = useTimeoutSettings();

  // Local state for the form
  const [formTimeoutHours, setFormTimeoutHours] = useState(timeoutHours);
  const [isSaving, setIsSaving] = useState(false);

  // Update form value when timeoutHours changes
  useEffect(() => {
    setFormTimeoutHours(timeoutHours);
  }, [timeoutHours]);

  // Redirect if not admin
  useEffect(() => {
    if (!sessionUser || !sessionUser.is_admin) {
      history.push('/');
    }
  }, [sessionUser, history]);

  // Set error message if there's an error from the hook
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const result = await updateTimeoutSetting(formTimeoutHours);

      if (result.success) {
        setSuccessMessage('Session timeout updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage(result.message);
      }
    } catch (error) {
      console.error('Error updating timeout setting:', error);
      setErrorMessage('An unexpected error occurred while saving settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-3xl text-blue-600">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 transition-all duration-300">
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5">
        <h1 className="text-3xl font-bold text-blue-800 mb-6 flex items-center">
          <FaClock className="mr-3" /> Admin Settings
        </h1>

        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 animate-[fadeIn_0.3s_ease-in-out]">
            {errorMessage}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 animate-[fadeIn_0.3s_ease-in-out]">
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaUserClock className="mr-2" /> Auto Logout Settings
            </h2>

            <div className="bg-blue-50 p-4 rounded-lg mb-6 border border-blue-200">
              <div className="flex items-start">
                <FaInfoCircle className="text-blue-600 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-blue-800 mb-2">About Auto Logout</h3>
                  <p className="text-gray-700 mb-2">
                    For security reasons, users are automatically logged out after a period of inactivity.
                    This helps protect user accounts and sensitive data when users forget to log out.
                  </p>
                  <p className="text-gray-700">
                    Users will receive a warning 5 minutes before being logged out, giving them a chance to continue their session.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-5 rounded-lg border border-gray-200 mb-6">
              <h3 className="font-semibold text-gray-800 mb-3 flex items-center">
                <FaSignOutAlt className="mr-2" /> Current Auto Logout Configuration
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <div className="text-gray-600 mb-1">Current timeout setting:</div>
                  <div className="text-2xl font-bold text-blue-700">{timeoutHours} hours</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <div className="text-gray-600 mb-1">Warning appears at:</div>
                  <div className="text-2xl font-bold text-orange-600">{(timeoutHours - (5/60)).toFixed(2)} hours</div>
                  <div className="text-sm text-gray-500">(5 minutes before logout)</div>
                </div>
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              Set the amount of time (in hours) before users are automatically logged out due to inactivity.
            </p>

            <div className="flex items-center">
              <label htmlFor="timeoutHours" className="block text-gray-700 text-lg mr-4">
                Inactivity Timeout:
              </label>
              <input
                id="timeoutHours"
                type="number"
                min="1"
                max="24"
                value={formTimeoutHours}
                onChange={(e) => setFormTimeoutHours(parseInt(e.target.value, 10))}
                className="shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight w-24 text-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="ml-2 text-lg text-gray-700">hours</span>
            </div>

            <div className="mt-4 bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-1 flex items-center">
                <FaInfoCircle className="mr-2" /> Recommendations
              </h4>
              <ul className="list-disc list-inside text-sm text-gray-700 ml-2">
                <li>2-4 hours: Recommended for high-security environments</li>
                <li>4-8 hours: Balanced security and convenience for most users</li>
                <li>8+ hours: Consider only for low-security environments</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className={`bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg text-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:shadow-none ${
                isSaving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;
